import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpenText, LoaderCircle, WandSparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  projectBookAssemblyStore,
  useProjectBookAssemblyStore,
} from "@/stores/project-book-assembly-store";

type BookAssemblyLaunchCardProps = {
  projectId: string;
};

function getLaunchStatusLabelKey(status: string | null | undefined) {
  switch (status) {
    case "complete":
      return "bookAssembly.launch.status.complete";
    case "failed":
      return "bookAssembly.launch.status.failed";
    case "processing":
      return "bookAssembly.launch.status.processing";
    case "pending":
      return "bookAssembly.launch.status.pending";
    default:
      return null;
  }
}

export function BookAssemblyLaunchCard({
  projectId,
}: BookAssemblyLaunchCardProps) {
  const { t } = useTranslation();
  const launchState = useProjectBookAssemblyStore(
    (state) => state.launchStates[projectId],
  );

  useEffect(() => {
    void projectBookAssemblyStore.getState().loadLaunchState(projectId);
  }, [projectId]);

  return (
    <Card className="overflow-hidden border-primary/20 bg-[radial-gradient(circle_at_top_left,rgba(164,84,28,0.16),transparent_55%),linear-gradient(135deg,rgba(255,248,236,0.96),rgba(255,255,255,0.92))]">
      <CardContent className="space-y-5 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">
              {t("bookAssembly.launch.eyebrow")}
            </p>
            <h2 className="text-3xl text-foreground">{t("bookAssembly.launch.title")}</h2>
            <p className="max-w-2xl text-sm leading-7 text-muted-foreground">
              {t("bookAssembly.launch.body")}
            </p>
          </div>
          <div className="rounded-[24px] border border-primary/15 bg-white/80 p-4 text-primary shadow-sm">
            <BookOpenText className="h-6 w-6" />
          </div>
        </div>

        {launchState ? (
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-[22px] border border-white/80 bg-white/80 px-4 py-4 text-sm text-muted-foreground">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-foreground/70">
                  {t("bookAssembly.launch.chapterCount")}
                </p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {launchState.chapterCount}
                </p>
              </div>
              <div className="rounded-[22px] border border-white/80 bg-white/80 px-4 py-4 text-sm text-muted-foreground">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-foreground/70">
                  {t("bookAssembly.launch.sideStoryCount")}
                </p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {launchState.sideStoryCount}
                </p>
              </div>
              <div className="rounded-[22px] border border-white/80 bg-white/80 px-4 py-4 text-sm text-muted-foreground">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-foreground/70">
                  {t("bookAssembly.launch.currentTier")}
                </p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {t(`common.tiers.${launchState.tier}`)}
                </p>
              </div>
              {launchState.latestJobStatus ? (
                <div
                  className="rounded-[22px] border border-white/80 bg-white/80 px-4 py-4 text-sm text-muted-foreground"
                  data-testid="book-assembly-launch-latest-job"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-foreground/70">
                    {t("bookAssembly.launch.latestJob")}
                  </p>
                  <p className="mt-2 text-lg font-semibold text-foreground">
                    {t(getLaunchStatusLabelKey(launchState.latestJobStatus) ?? "bookAssembly.launch.ready")}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="space-y-3 lg:text-right">
              <p className="max-w-md text-sm leading-6 text-muted-foreground lg:ml-auto">
                {launchState.availability.reason
                  ? t(launchState.availability.reason)
                  : t(`bookAssembly.launch.tierSummary.${launchState.tier}`)}
              </p>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                {launchState.availability.eligible ? (
                  <Button asChild size="lg" type="button">
                    <Link to={launchState.href}>
                      <WandSparkles className="h-4 w-4" />
                      {launchState.latestJobStatus
                        ? t("bookAssembly.launch.resume")
                        : t("bookAssembly.launch.open")}
                    </Link>
                  </Button>
                ) : (
                  <Button disabled size="lg" type="button">
                    {t("bookAssembly.launch.open")}
                  </Button>
                )}
                {launchState.recommendedUpgradeTier ? (
                  <Button asChild size="lg" type="button" variant="outline">
                    <Link to="/account/billing">
                      {t("bookAssembly.launch.upgrade", {
                        tier: t(`common.tiers.${launchState.recommendedUpgradeTier}`),
                      })}
                    </Link>
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-[22px] border border-white/80 bg-white/80 px-4 py-4 text-sm text-muted-foreground">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            {t("bookAssembly.launch.loading")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
